import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCurrentStore, updateCurrentStore } from '@/lib/api/stores.js';
import { listUsers } from '@/lib/api/users.js';
import { listStaffPinStatuses, resetStaffPin, type StaffPinStatus } from '@/lib/api/pin.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { prettyLabel } from '@/lib/format.js';
import { useAuthStore } from '@/stores/auth.js';
import { canManageStoreRole } from '@/lib/deposit.js';
import { cn } from '@/lib/utils.js';

const PIN_ACTIONS = [
  { key: 'requirePinForCash', label: 'Taking a cash payment' },
  { key: 'requirePinForRefunds', label: 'Issuing a refund' },
  { key: 'requirePinForVoids', label: 'Voiding a ticket' },
  { key: 'requirePinForDiscounts', label: 'Applying discount (staff confirmation)' },
] as const;

type StaffRow = {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  hasPinSet?: boolean;
  locked?: boolean;
  lockedUntil?: string | null;
};

function pinStatusLabel(row: StaffRow): { label: string; className: string } {
  if (row.locked) {
    return { label: 'Locked', className: 'bg-rose-100 text-rose-800' };
  }
  if (row.hasPinSet) {
    return { label: 'PIN set', className: 'bg-emerald-100 text-emerald-800' };
  }
  if (row.hasPinSet === false) {
    return { label: 'Not set', className: 'bg-slate-100 text-slate-600' };
  }
  return { label: '—', className: 'bg-slate-100 text-slate-500' };
}

export function PinSettingsPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isManager = canManageStoreRole(user?.role);

  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: listUsers,
    enabled: !isManager,
  });
  const pinStatusesQuery = useQuery({
    queryKey: ['staff-pin-statuses'],
    queryFn: listStaffPinStatuses,
    enabled: isManager,
  });
  const store = storeQuery.data;

  const [security, setSecurity] = useState({
    requirePinForCash: false,
    requirePinForRefunds: false,
    requirePinForVoids: false,
    requirePinForDiscounts: false,
  });

  useEffect(() => {
    if (!store) return;
    setSecurity({
      requirePinForCash: Boolean(store.requirePinForCash),
      requirePinForRefunds: Boolean(store.requirePinForRefunds),
      requirePinForVoids: Boolean(store.requirePinForVoids),
      requirePinForDiscounts: Boolean(store.requirePinForDiscounts),
    });
  }, [store]);

  const mutation = useMutation({
    mutationFn: () => updateCurrentStore(security),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      toastSuccess('PIN settings saved');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const unlockMutation = useMutation({
    mutationFn: (userId: string) => resetStaffPin(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-pin-statuses'] });
      toastSuccess('PIN unlocked');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const resetPinMutation = useMutation({
    mutationFn: (userId: string) => resetStaffPin(userId, { clearPin: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-pin-statuses'] });
      toastSuccess('PIN cleared — staff must set a new PIN');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const staffRows: StaffRow[] = useMemo(() => {
    if (isManager) {
      return (pinStatusesQuery.data ?? []).map((s: StaffPinStatus) => ({
        id: s.userId,
        firstName: s.firstName,
        lastName: s.lastName,
        role: s.role,
        hasPinSet: s.hasPinSet,
        locked: s.locked,
        lockedUntil: s.lockedUntil,
      }));
    }
    return (usersQuery.data ?? [])
      .filter((u) => u.active !== false)
      .map((u) => ({
        id: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        role: u.role,
      }));
  }, [isManager, pinStatusesQuery.data, usersQuery.data]);

  const listLoading = isManager ? pinStatusesQuery.isLoading : usersQuery.isLoading;
  const busyUserId =
    unlockMutation.isPending && unlockMutation.variables
      ? unlockMutation.variables
      : resetPinMutation.isPending && resetPinMutation.variables
        ? resetPinMutation.variables
        : null;

  return (
    <form
      className="space-y-6"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-bold text-slate-950">Staff PIN (Your own PIN)</h2>
        <p className="mt-1 text-sm text-slate-500">
          Staff enter <span className="font-semibold">their own</span> PIN to confirm it is really
          them — for example when taking cash. This is separate from manager override.
        </p>
        <div className="mt-4 space-y-2">
          {PIN_ACTIONS.map(({ key, label }) => (
            <label
              key={key}
              className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium"
            >
              <span>{label}</span>
              <input
                type="checkbox"
                checked={security[key]}
                onChange={(e) => setSecurity((c) => ({ ...c, [key]: e.target.checked }))}
                disabled={!isManager}
              />
            </label>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-bold text-slate-950">Manager Override PIN</h2>
        <p className="mt-1 text-sm text-slate-500">
          A <span className="font-semibold">manager</span> enters <span className="font-semibold">their</span>{' '}
          PIN to authorize a restricted action for a lower-level staff member. The staff member stays
          logged in. Configure thresholds and roles under Permissions.
        </p>
        <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-slate-700">
          <li>Discount exceeds the store maximum %</li>
          <li>Voiding a ticket (when role is not allowed to void alone)</li>
          <li>Issuing refunds (when role is not allowed alone)</li>
          <li>Price overrides below permission / when manager override is required</li>
          <li>Waiving a required deposit</li>
        </ul>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-bold text-slate-950">Staff PIN Status</h2>
        <p className="mt-1 text-sm text-slate-500">
          Staff set their PIN in My Profile.
          {isManager
            ? ' Managers can unlock a locked PIN or clear it so the staff member sets a new one.'
            : ''}
        </p>
        {listLoading ? (
          <p className="mt-4 text-sm text-slate-500">Loading staff…</p>
        ) : (
          <ul className="mt-4 divide-y divide-slate-100 rounded-xl border border-slate-200">
            {staffRows.map((u) => {
              const status = pinStatusLabel(u);
              const unlocking = busyUserId === u.id;
              return (
                <li
                  key={u.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"
                >
                  <div className="min-w-0">
                    <span className="font-medium">
                      {u.firstName} {u.lastName}
                    </span>
                    <span className="ml-2 text-slate-500 capitalize">{prettyLabel(u.role)}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={cn(
                        'rounded-full px-2.5 py-0.5 text-xs font-semibold',
                        status.className,
                      )}
                    >
                      {status.label}
                    </span>
                    {isManager && u.locked ? (
                      <button
                        type="button"
                        disabled={Boolean(busyUserId)}
                        onClick={() => unlockMutation.mutate(u.id)}
                        className="rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                      >
                        {unlocking && unlockMutation.isPending ? 'Unlocking…' : 'Unlock PIN'}
                      </button>
                    ) : null}
                    {isManager && (u.locked || u.hasPinSet) ? (
                      <button
                        type="button"
                        disabled={Boolean(busyUserId)}
                        onClick={() => {
                          if (
                            !window.confirm(
                              `Clear PIN for ${u.firstName} ${u.lastName}? They will need to set a new PIN in My Profile.`,
                            )
                          ) {
                            return;
                          }
                          resetPinMutation.mutate(u.id);
                        }}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        {unlocking && resetPinMutation.isPending ? 'Resetting…' : 'Reset PIN'}
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
            {staffRows.length === 0 ? (
              <li className="px-4 py-3 text-sm text-slate-500">No active staff found.</li>
            ) : null}
          </ul>
        )}
      </section>

      {isManager ? (
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded-xl bg-primary px-6 py-2.5 font-semibold text-white disabled:opacity-50"
        >
          {mutation.isPending ? 'Saving…' : 'Save PIN Settings'}
        </button>
      ) : null}
    </form>
  );
}
